import React, { FC, useCallback, useEffect, useState } from 'react';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import TwField, { StyledTextfieldLabel, TwInputTypes } from '../TwFields/TwFields';
import Stack from '@mui/material/Stack';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TwDialog from '../TwDialog/TwDialog';
import TwRadioButton from '../TwRadioButtons/TwRadioButtons';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import LoadsService from '../../providers/Loads/Loads.service';
import { SliceStop, updateEstimatedMileageSegment } from '../../redux/features/orders/ordersSlice';
import { useAppDispatch } from '../../redux/hooks';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import { updateTripLoadedMiles } from '../../redux/features/trip/tripSlice';
import { EntityType } from '../../redux/Global.model';
import useAuth from '../../providers/Auth/Auth.provider';
import { Characters } from '../../hooks/useAllowCharacters';
import { useAllowCharacters } from '../../hooks/useAllowCharacters';
import { getTooltipText } from '../../utils/mileage/mileageUtils';
import TwTooltip from '../TwTooltip/TwTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PROVIDER } from '../../providers/User/User.model';
import { useGetTenantRoutingSettingsQuery } from '../../redux/api/tenant/tenantApi';

interface TwLoadedMilesProps {
  stops: SliceStop[];
  initialLoadedMiles: number | null;
  initialAutoCalculate: boolean;
  initialSource: PROVIDER | null;
  segmentId: string;
  initialStopOrder?: number;
  entity: EntityType;
}

const TwLoadedMiles: FC<TwLoadedMilesProps> = ({
  stops,
  initialLoadedMiles,
  initialAutoCalculate,
  initialSource,
  segmentId = '',
  initialStopOrder = 1,
  entity
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const isAutoCalculateMileageDisabled =
    (userData?.tenantSettings && userData.tenantSettings['disable-auto-calculate-mileage']) ||
    false;
  const dispatch = useAppDispatch();
  const { enablePcMiler } = useFeatureFlags();
  const { addNotification } = useNotifications();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const [loadedMiles, setLoadedMiles] = useState<number | undefined>(undefined);
  const [tempLoadedMiles, setTempLoadedMiles] = useState<number | undefined>(undefined);
  const [openCalculateModal, setOpenCalculateModal] = useState<boolean>(false);
  const [option, setOption] = useState<string>(
    isAutoCalculateMileageDisabled ? 'manually' : 'automatically'
  );
  const [tooltip, setTooltip] = useState<string>('');
  const { data: routingSettings } = useGetTenantRoutingSettingsQuery(null);
  const [provider, setProvider] = useState<PROVIDER>(
    !enablePcMiler ? PROVIDER.GOOGLE : routingSettings?.provider ?? PROVIDER.GOOGLE
  );

  const areAllStopsAssigned = stops.every(
    (stop) => stop.provider_location && stop.provider_location.id !== null
  );
  const firstStop = stops[0];
  const lastStop = stops[stops.length - 1];

  const getLoadedMiles = useCallback(() => {
    if (firstStop.provider_location?.id && lastStop.provider_location?.id && stops.length > 0) {
      const intermediateStopsIds: string[] = [];

      for (let i = 1; i < stops.length - 1; i++) {
        if (stops[i].provider_location?.id) {
          intermediateStopsIds.push(stops[i].provider_location?.id || '');
        }
      }

      LoadsService.getEstimatedMilage(
        firstStop.provider_location.id,
        lastStop.provider_location.id,
        intermediateStopsIds
      )
        .then((res) => {
          setTempLoadedMiles(Number(parseFloat(res.milage).toFixed(2)));
        })
        .catch((err) => {
          console.log('err', err);
          setTempLoadedMiles(0);
          addNotification({
            message: err.message || 'Error calculating loaded miles, please try again',
            variant: 'error'
          });
        });
    }
  }, [
    firstStop?.location_id,
    lastStop?.location_id,
    JSON.stringify(stops.map((stop) => stop.ui_id))
  ]);

  const handleKeyDownCharacters = useAllowCharacters(Characters.float);

  useEffect(() => {
    setLoadedMiles(initialLoadedMiles === null ? undefined : initialLoadedMiles);
  }, [initialLoadedMiles]);

  useEffect(() => {
    if (option === 'automatically' && openCalculateModal) {
      getLoadedMiles();
    }
  }, [openCalculateModal, option, getLoadedMiles]);

  useEffect(() => {
    if (openCalculateModal && loadedMiles !== undefined) {
      setTempLoadedMiles(Number(loadedMiles.toFixed(2)));
    }
  }, [openCalculateModal, loadedMiles]);

  useEffect(() => {
    if (initialLoadedMiles === undefined || !initialSource) return;
    setTooltip(getTooltipText('Loaded miles', initialSource, initialAutoCalculate));
  }, [initialLoadedMiles, initialSource, initialAutoCalculate]);

  useEffect(() => {
    setProvider(!enablePcMiler ? PROVIDER.GOOGLE : routingSettings?.provider ?? PROVIDER.GOOGLE);
  }, [enablePcMiler, routingSettings?.provider]);

  const handleOpenCalculatorModal = (): void => {
    if (!areAllStopsAssigned) {
      addNotification({
        message: 'All stops locations are required to calculate loaded miles',
        variant: 'error'
      });
      return;
    }

    setOpenCalculateModal(true);
  };

  const handleCloseCalculatorModal = (): void => {
    setOpenCalculateModal(false);
  };

  const handleSaveLoadedMiles = (): void => {
    setOpenCalculateModal(false);
    setTooltip(getTooltipText('Loaded miles', provider, option === 'automatically'));

    if (entity === EntityType.TRIP && enableLtl) {
      dispatch(
        updateTripLoadedMiles({
          value: tempLoadedMiles === undefined ? null : Number(tempLoadedMiles.toFixed(2)),
          is_auto: option === 'automatically',
          source: provider
        })
      );
    } else {
      dispatch(
        updateEstimatedMileageSegment({
          segmentId: segmentId,
          value: tempLoadedMiles === undefined ? null : Number(tempLoadedMiles.toFixed(2)),
          is_auto: option === 'automatically',
          source: provider
        })
      );
    }
  };

  return (
    <>
      <Stack>
        <StyledTextfieldLabel style={{ display: 'flex', alignItems: 'center' }}>
          Loaded miles
          {loadedMiles && enablePcMiler ? (
            <TwTooltip title={tooltip} placement="top">
              <InfoOutlinedIcon sx={{ height: '16px' }} />
            </TwTooltip>
          ) : null}
        </StyledTextfieldLabel>
        {loadedMiles === undefined && (
          <TwButton
            type={TwButtonType.secondary}
            label="Calculate"
            action={handleOpenCalculatorModal}
          />
        )}

        {loadedMiles !== undefined && (
          <TwField
            inputType={TwInputTypes.number}
            name="loaded-miles"
            endAdorment={
              <EditIcon
                sx={{ position: 'relative', left: '5px', cursor: 'pointer' }}
                onClick={handleOpenCalculatorModal}
              />
            }
            value={loadedMiles.toFixed(2)}
            disabled
          />
        )}
      </Stack>

      <TwDialog
        open={openCalculateModal}
        onClickClose={handleCloseCalculatorModal}
        title="Calculate loaded miles"
        classNameActions="empty-miles__actions"
        actions={[
          <TwButton
            key="1"
            type={TwButtonType.secondary}
            label="Cancel"
            action={handleCloseCalculatorModal}
          />,
          <TwButton
            key="2"
            type={TwButtonType.primary}
            label="Save"
            action={handleSaveLoadedMiles}
          />
        ]}
      >
        <Stack className="empty-miles__options-container">
          <Stack className="empty-miles__options" flexDirection={'row'}>
            <TwRadioButton
              value={'manually'}
              checked={option === 'manually'}
              name="calculate-manually-loaded"
              label=""
              onInputChange={(): void => setOption('manually')}
            />
            <Box>
              <Typography fontWeight={'500'} fontSize={'14px'}>
                Enter miles manually
              </Typography>
              <Typography fontSize={'14px'} color={'#6B7280'}>
                Add the mileage by yourself
              </Typography>
            </Box>
          </Stack>

          {!isAutoCalculateMileageDisabled && (
            <Stack className="empty-miles__options" flexDirection={'row'}>
              <TwRadioButton
                value={'automatically'}
                checked={option === 'automatically'}
                name="calculate-automatically-loaded"
                label=""
                onInputChange={(): void => setOption('automatically')}
              />
              <Box>
                <Typography fontWeight={'500'} fontSize={'14px'}>
                  Calculate automatically
                </Typography>
                <Typography fontSize={'14px'} color={'#6B7280'}>
                  Use created addresses to calculate mileage
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>

        {option === 'manually' ? (
          <TwField
            inputType={TwInputTypes.number}
            name="loaded-miles-calculation"
            label="Loaded milage"
            value={tempLoadedMiles}
            onInputChange={(e): void => {
              if (e.target.value) {
                setTempLoadedMiles(Number(e.target.value));
              } else {
                setTempLoadedMiles(undefined);
              }
            }}
            sx={{ width: '180px' }}
            onKeyDown={handleKeyDownCharacters}
          />
        ) : (
          <>
            {stops.map((stop, index) => (
              <React.Fragment key={stop.ui_id}>
                <StyledTextfieldLabel>Stop {initialStopOrder + index}</StyledTextfieldLabel>
                <Box className="empty-miles__result-container">
                  <StyledTextfieldLabel>
                    {stop.provider_location && stop.provider_location.label}
                  </StyledTextfieldLabel>
                </Box>
              </React.Fragment>
            ))}

            <Stack
              flexDirection={'row'}
              justifyContent={'space-between'}
              className="empty-miles__result-container"
            >
              <Box>
                <StyledTextfieldLabel>Loaded miles</StyledTextfieldLabel>
              </Box>
              <Typography fontWeight={'600'}>
                {tempLoadedMiles && `${tempLoadedMiles.toFixed(2)} mi`}
              </Typography>
            </Stack>
          </>
        )}
      </TwDialog>
    </>
  );
};

export default TwLoadedMiles;
